export const TRACE_PARENT_HTTP_HEADER: string = 'traceparent'
export const CLOUD_TRACE_HEADER: string = 'x-cloud-trace-context'

const VERSION_PART = '(?!ff)[\\da-f]{2}'
const TRACE_ID_PART = '(?![0]{32})[\\da-f]{32}'
const PARENT_ID_PART = '(?![0]{16})[\\da-f]{16}'
const FLAGS_PART = '[\\da-f]{2}'

// only match the spanId part - must be exactly 16 hex digits, not all zeros
const LOAD_BALANCER_SPAN_ID_REGEX = new RegExp(`^\\s?(${PARENT_ID_PART})\\s?$`)
const TRACE_PARENT_REGEX = new RegExp(
  `^\\s?(${VERSION_PART})-(${TRACE_ID_PART})-(${PARENT_ID_PART})-(${FLAGS_PART})(-.*)?\\s?$`,
)

/**
 * Finds the span id from the incoming headers.
 *
 * Prioritizes the internal load balancer span id header if it is present.
 *
 * @param headers - The incoming headers.
 * @param loadBalancerSpanIdHeaderName - The name of the internal load balancer spanId header to use
 * @returns The span id or undefined if it cannot be found.
 */
export function findSpanIdFromIncomingHeaders(
  headers: Record<string, string>,
  loadBalancerSpanIdHeaderName: string,
): string | undefined {
  // if the load balancer span id header is present, use it
  const loadBalancerSpanId = parseLoadBalancerSpanId(headers[loadBalancerSpanIdHeaderName])

  // if the load balancer span id is valid, use it
  if (loadBalancerSpanId) {
    return loadBalancerSpanId
  }

  const traceParent = headers[TRACE_PARENT_HTTP_HEADER]

  // if the traceparent header is present, use it
  const spanId = parseTraceParentSpanId(traceParent)

  // if the traceparent spanId is valid, use it
  if (spanId) {
    return spanId
  }

  const cloudTrace = headers[CLOUD_TRACE_HEADER]

  // if the cloud trace header is present, use it
  const cloudTraceSpanId = parseCloudTraceSpanId(cloudTrace)

  // if the cloud trace spanId is valid, use it
  if (cloudTraceSpanId) {
    return cloudTraceSpanId
  }

  // if neither are present, return undefined
  return undefined
}

/**
 * Parses the load balancer span id from our internal header.
 *
 * @param value - The value to parse.
 * @returns The load balancer span id or undefined if the value is not a valid load balancer span id.
 */
export function parseLoadBalancerSpanId(value: string): string | undefined {
  const match = LOAD_BALANCER_SPAN_ID_REGEX.exec(value)
  if (!match) {
    return undefined
  }

  return match[1]
}

/**
 * Parses the span id from the traceparent header.
 *
 * @param traceParent - The traceparent header value.
 * @returns The span id or undefined if the value is not a valid traceparent.
 */
export function parseTraceParentSpanId(traceParent: string): string | undefined {
  const match = TRACE_PARENT_REGEX.exec(traceParent)
  if (!match) {
    return undefined
  }

  // According to the specification the implementation should be compatible
  // with future versions. If there are more parts, we only reject it if it's using version 00
  // See https://www.w3.org/TR/trace-context/#versioning-of-traceparent
  if (match[1] === '00' && match[5]) {
    return undefined
  }

  return match[3]
}

/**
 * Parses the span id from the X-Cloud-Trace-Context header.
 
 * The X-Cloud-Trace-Context header has the following format:
 *
 * X-Cloud-Trace-Context: TRACE_ID/SPAN_ID;o=OPTIONS
 *
 * The fields of header are defined as follows:
 *
 * TRACE_ID is a 32-character hexadecimal value representing a 128-bit number.
 * SPAN_ID is a 64-bit decimal representation of the unsigned span ID.
 * OPTIONS supports 0 (parent not sampled) and 1 (parent was sampled).
 *
 * @param value - The value to parse.
 * @returns The span id or undefined if the value is not a valid X-Cloud-Trace-Context header.
 */
export function parseCloudTraceSpanId(value: string): string | undefined {
  if (!value) {
    return undefined
  }

  // Split on the optional options part
  const parts = value.split(';')

  // Split the trace/span part
  const traceSpanParts = parts[0].split('/')

  // Check if we have a valid format with at least trace and span parts
  if (traceSpanParts.length !== 2) {
    return undefined
  }

  const spanId = traceSpanParts[1].trim()

  // Validate that the span ID is a valid decimal number
  if (!/^\d+$/.test(spanId) || spanId === '0') {
    return undefined
  }

  // Convert decimal span ID to 16-character hex representation
  // First parse as BigInt to handle 64-bit values properly
  try {
    const spanIdBigInt = BigInt(spanId)
    // Convert to hex and pad to 16 characters
    return spanIdBigInt.toString(16).padStart(16, '0')
  } catch {
    return undefined
  }
}
